/**
 * useDialogueSession — orchestrateur du dialogue guidé (US-VDPI-03).
 *
 * Colle le réducteur PUR `dialogueMachine` aux briques vocales EXISTANTES (DRY) :
 *   - boucle wake (`useWakeWord`) : réveil "saisi"
 *   - capture micro + VAD (`VoiceSessionManager` via une session minimale)
 *   - `speak()` (PersonaContext)
 *   - earcons (`playEarcon`)
 *   - backend (`dpiService`, endpoints US-VDPI-02)
 *
 * Le réducteur calcule état + effets ; ce hook EXÉCUTE les effets et réinjecte
 * les résultats (DATA_*, COMMIT_OK) comme nouveaux events. Re-arm wake auto.
 *
 * Réf : docs/meta/plan/PLAN-VOCAL-DPI-JSON-2026-06-17.md §5 §13.1.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePersona } from '@/shared/contexts/PersonaContext';
import { dpiService } from '@/shared/services/dpiService';
import {
  dialogueReducer,
  initialState,
  orbeStateFor,
  type DialogueContext,
  type DialogueEffect,
  type DialogueEvent,
  type DialogueState,
  type DialogueStateName,
  type OrbeState,
} from '../services/dialogueMachine';
import { parseControl } from '../services/parseControl';
import { parseIntent } from '../services/parseIntent';
import { parseSpokenInt } from '../services/parseNumber';
import { captureModeFor } from '../services/captureSpec';
import { playEarcon } from '../services/earcons';
import { VoiceSessionManager } from '../services/VoiceSessionManager';
import { useWakeWord } from './useWakeWord';

/**
 * États « réponse attendue » (col. Auto=oui, table SSOT §3) : à l'entrée de ces
 * états, l'écoute se ré-ouvre AUTOMATIQUEMENT à la fin du TTS (auto-écoute, spec §2)
 * — zéro wake. Les états fetch/busy (SELECT_ROOM, MENU) et IDLE en sont exclus.
 * UNION des deux moteurs ci-dessous (sert à accrocher l'onEnd du dernier speak).
 */
const AUTO_LISTEN_STATES: ReadonlySet<DialogueStateName> = new Set<DialogueStateName>([
  'CONFIRM_PATIENT',
  'SELECT_PATIENT',
  'AWAIT_ACTION',
  'CAPTURE',
  'AWAIT_COMMIT',
]);

/** Moteur VAD (capture-dictée) : énoncés libres / champs guidés (silence = fin). */
const VAD_LISTEN_STATES: ReadonlySet<DialogueStateName> = new Set<DialogueStateName>([
  'CAPTURE',
]);

/**
 * Moteur word-spotting wake (Q-1 audit §7.4) : les mots de contrôle courts sont
 * écoutés via le flux wake (overlap, ~0.5s) → supprime la falaise VAD + le
 * boundary-split. PAS de capture VAD ouverte ici (`capturing` reste false → le
 * wake garde le micro).
 */
const COMMAND_LISTEN_STATES: ReadonlySet<DialogueStateName> = new Set<DialogueStateName>([
  'CONFIRM_PATIENT',
  'SELECT_PATIENT',
  'AWAIT_ACTION',
  'AWAIT_COMMIT',
]);

/** Extrait le numéro de chambre d'un transcript : chiffres (« chambre 101 ») OU
 *  mots-nombres français (« chambre cent un » → 101), via parseSpokenInt (JS pur). */
function extractRoom(transcript: string): string | null {
  const n = parseSpokenInt(transcript);
  return n !== null ? String(n) : null;
}

export interface DialogueSession {
  /** État visuel de l'orbe (AC5). */
  orbe: OrbeState;
  /** Dernier message système (echo du speak), pour la ligne UI. */
  prompt: string;
  /** Patient courant (repère discret présentateur). */
  patientLabel: string | null;
  /** Micro actif (capture VAD en cours). */
  capturing: boolean;
  /** Écoute commande wake armée (état de contrôle) — orbe vert sans capture VAD (Q-1). */
  awaitingCommand: boolean;
  /** Traitement async en cours (fetch/commit) — pulsation orbe (B1). */
  busy: boolean;
  /** Boucle wake armée. */
  listening: boolean;
  /** Erreur courante. */
  error: string | null;
  /** Phase logique courante du dialogue (pilote les panneaux contextuels A2). */
  phase: DialogueStateName;
  /**
   * Projection LECTURE SEULE du contexte du dialogue (habilitant A1, plan §4.1).
   * Permet à l'écran d'afficher les vraies données DPI au rythme du dialogue
   * sans dupliquer la logique métier (panneaux pilotés par `ctx`).
   */
  ctx: DialogueContext;
  /** Entrée texte : même routage qu'une transcription vocale (US-VMPL-002). */
  entendu: (texte: string) => void;
}

export function useDialogueSession(enabled: boolean): DialogueSession {
  const { speak, stopSpeaking } = usePersona();
  const speakRef = useRef(speak);
  useEffect(() => { speakRef.current = speak; }, [speak]);
  const stopSpeakingRef = useRef(stopSpeaking);
  useEffect(() => { stopSpeakingRef.current = stopSpeaking; }, [stopSpeaking]);

  const [state, setState] = useState<DialogueState>(initialState);
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  const [capturing, setCapturing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Mode commande wake armé (Q-1) : l'orbe doit rester vert (« parle ») comme en
   *  capture VAD, alors que `capturing` est false. */
  const [awaitingCommand, setAwaitingCommand] = useState(false);

  /** Compteur d'effets async en vol (fetch/commit) → signal `busy` (B1). */
  const busyCountRef = useRef(0);
  const beginBusy = useCallback(() => { busyCountRef.current += 1; setBusy(true); }, []);
  const endBusy = useCallback(() => {
    busyCountRef.current = Math.max(0, busyCountRef.current - 1);
    if (busyCountRef.current === 0) setBusy(false);
  }, []);

  /** Session vocale active (une à la fois). */
  const sessionRef = useRef<VoiceSessionManager | null>(null);
  /** true si la prochaine capture est l'initiale (post-wake → extraire chambre). */
  const awaitingRoomRef = useRef(false);
  /** Auto-écoute (E1) : ouvre une capture de réponse à la fin du TTS d'entrée
   *  d'un état Auto=oui. Réf différée (startCapture défini plus bas). */
  const autoListenRef = useRef<() => void>(() => {});
  /** Mode commande wake (Q-1) : impératifs du hook wake (assignés après son appel)
   *  + latch anti-double-tir (un seul dispatch par écoute armée). */
  const armCommandRef = useRef<() => void>(() => {});
  const disarmCommandRef = useRef<() => void>(() => {});
  const commandConsumedRef = useRef(false);

  // ── dispatch : applique le réducteur puis exécute les effets ───────────────
  const dispatchRef = useRef<(e: DialogueEvent) => void>(() => {});

  const runEffects = useCallback((effects: DialogueEffect[], nextName: DialogueStateName) => {
    // Auto-écoute (E1) : si le prochain état attend une réponse, on accroche
    // l'ouverture de capture à la fin du DERNIER `speak` d'entrée (et lui seul,
    // pour ne pas ouvrir prématurément sur un `speak` intermédiaire).
    const autoListen = AUTO_LISTEN_STATES.has(nextName);
    const lastSpeakIdx = autoListen
      ? effects.map((e) => e.type).lastIndexOf('speak')
      : -1;
    effects.forEach((fx, i) => {
      switch (fx.type) {
        case 'speak':
          speakRef.current(fx.text, i === lastSpeakIdx ? autoListenRef.current : undefined);
          break;
        case 'earcon':
          playEarcon(fx.sound);
          break;
        case 'rearmWake':
          // La boucle wake reste armée tant que `enabled` ; rien à faire (re-arm auto).
          break;
        case 'fetchChambre':
          beginBusy();
          dpiService.chambre(fx.numero).then((chambre) => {
            if (chambre) dispatchRef.current({ type: 'DATA_CHAMBRE', chambre });
            else dispatchRef.current({ type: 'DATA_CHAMBRE_NOT_FOUND', numero: fx.numero });
          }).catch(() => dispatchRef.current({ type: 'DATA_CHAMBRE_NOT_FOUND', numero: fx.numero }))
            .finally(endBusy);
          break;
        case 'fetchMenu':
          beginBusy();
          dpiService.menu(fx.patientId).then((m) => {
            dispatchRef.current({ type: 'DATA_MENU', menu: m.items });
          }).catch(() => {
            setError('Menu indisponible');
            playEarcon('error');
            speakRef.current('Menu indisponible.');
          }).finally(endBusy);
          break;
        case 'commit':
          beginBusy();
          dpiService.saisie({
            patient_id: fx.patient.patient_id,
            chambre: fx.chambre,
            section: (fx.item.section as never),
            data: fx.data,
            statut: 'valide',
          }).then(() => dispatchRef.current({ type: 'COMMIT_OK' }))
            .catch(() => {
              setError('Enregistrement échoué');
              playEarcon('error');
              speakRef.current("L'enregistrement a échoué.");
            }).finally(endBusy);
          break;
      }
    });
  }, [beginBusy, endBusy]);

  const dispatch = useCallback((event: DialogueEvent) => {
    const { state: next, effects } = dialogueReducer(stateRef.current, event);
    stateRef.current = next;
    setState(next);
    runEffects(effects, next.name);
  }, [runEffects]);
  dispatchRef.current = dispatch;

  // « stop » = turn OFF (Option 1) : coupe la SAISIE (TTS + capture) → ARMÉ.
  // Le TRAITEMENT async déjà lancé (commit POST) n'est PAS annulé : il se termine
  // seul (la donnée s'écrit) ; son résultat tardif est ignoré par les gardes d'état
  // du réducteur. Après « stop », redire « saisi » pour relancer un scénario.
  const stopFlow = useCallback(() => {
    stopSpeakingRef.current();
    sessionRef.current?.destroy();
    sessionRef.current = null;
    setCapturing(false);
    // Coupe aussi le mode commande wake (Q-1) → retour ARMÉ propre.
    commandConsumedRef.current = false;
    setAwaitingCommand(false);
    disarmCommandRef.current();
    dispatchRef.current({ type: 'STOP' });
  }, []);
  const stopFlowRef = useRef(stopFlow);
  useEffect(() => { stopFlowRef.current = stopFlow; }, [stopFlow]);

  /** Routage du texte capturé (DRY : voix et entrée texte, US-VMPL-002). */
  const routeCaptured = useCallback((text: string, asRoom: boolean) => {
    if (asRoom) {
      const numero = extractRoom(text);
      if (import.meta.env.DEV) console.log('[VOICE] extractRoom →', numero);
      if (numero) dispatchRef.current({ type: 'WAKE_ROOM', numero });
      else speakRef.current("Je n'ai pas compris le numéro de chambre.");
      return;
    }

    // Le moteur VAD ne sert plus QUE pour la CAPTURE (champ guidé / dictée
    // narrative, énoncés où le silence = fin). Les mots de contrôle des 4 états
    // de contrôle passent désormais par le moteur wake (onCommand, Q-1 §7.4).
    const cur = stateRef.current;
    if (cur.name === 'CAPTURE') {
      if (!text) {
        speakRef.current("Je n'ai rien entendu.");
        return;
      }
      const section = cur.ctx.action?.section ?? '';
      if (captureModeFor(section) === 'guided') {
        dispatchRef.current({ type: 'CAPTURE_FIELD', value: text });
      } else {
        dispatchRef.current({ type: 'CAPTURE_TEXT', text });
      }
    }
  }, []);

  // ── Boucle wake : "saisi" détecté → ouvrir une capture d'amorce ────────────
  const startCapture = useCallback(async (asRoom: boolean) => {
    if (sessionRef.current) return; // déjà en capture
    awaitingRoomRef.current = asRoom;
    // Coupe proprement le TTS en cours (V1) : neutralise les handlers AVANT cancel
    // → pas de faux `onEnd` qui relancerait une capture concurrente (double getUserMedia).
    stopSpeakingRef.current();

    const manager = new VoiceSessionManager({
      onChunkComplete: (partialText) => {
        // DEV only — visibilité sur ce que Vosk entend en temps réel.
        if (import.meta.env.DEV) console.log('[VOICE] partiel:', JSON.stringify(partialText));
      },
      onSessionComplete: (result) => {
        sessionRef.current = null;
        setCapturing(false);
        const text = result.fullText.trim();
        // DEV only — ce que Vosk a réellement transcrit + contexte.
        if (import.meta.env.DEV) {
          console.log('[VOICE] transcription finale:', JSON.stringify(text),
            '| phase:', stateRef.current.name, '| asRoom:', awaitingRoomRef.current,
            '| latence(s):', result.processingTime);
        }

        const asRoom = awaitingRoomRef.current;
        awaitingRoomRef.current = false;
        routeCaptured(text, asRoom);
      },
      onError: (msg) => {
        sessionRef.current = null;
        setCapturing(false);
        setError(msg);
        playEarcon('error');
      },
      // « stop » dit pendant la capture (turn OFF) → fermeture du flux (DRY).
      onStopWord: () => stopFlowRef.current(),
    });

    sessionRef.current = manager;
    try {
      await manager.start();
      setCapturing(true);
      setError(null);
      // Bip d'ouverture quand le micro de capture est RÉELLEMENT prêt (B3 +
      // fix clipping : avant, le bip partait dans onWakeWord, avant getUserMedia,
      // donc les 1ers mots étaient prononcés micro pas encore ouvert).
      playEarcon('saisi');
    } catch (err) {
      sessionRef.current = null;
      setCapturing(false);
      setError(err instanceof Error ? err.message : 'Erreur micro');
      playEarcon('error');
    }
  }, [routeCaptured]);

  // Auto-écoute (E1, spec §2) : ré-ouvre la capture à la fin du TTS d'entrée d'un
  // état Auto=oui. Garde : jamais si déjà en capture, ni si l'état a déjà changé
  // (un nouveau speak peut avoir transité ailleurs entre-temps).
  const maybeOpenResponseCapture = useCallback(() => {
    if (sessionRef.current) return;
    const name = stateRef.current.name;
    if (COMMAND_LISTEN_STATES.has(name)) {
      // État de contrôle → écoute via le moteur wake (overlap). Reset latch + arm
      // (reset buffer backend = écarte l'écho du prompt qu'on vient de prononcer).
      commandConsumedRef.current = false;
      setAwaitingCommand(true);
      armCommandRef.current();
      return;
    }
    if (VAD_LISTEN_STATES.has(name)) void startCapture(false);
  }, [startCapture]);
  autoListenRef.current = maybeOpenResponseCapture;

  // Wake "saisi" = RÉVEIL d'une nouvelle saisie → uniquement depuis IDLE (spec §2 :
  // « wake une fois » ; les tours de parole passent par l'auto-écoute, zéro wake).
  const onWakeWord = useCallback(() => {
    if (sessionRef.current) return;
    if (stateRef.current.name !== 'IDLE') return;
    void startCapture(true);
  }, [startCapture]);

  // Mode commande wake (Q-1 audit §7.4) : transcript renvoyé par la route wake
  // pendant un état de contrôle. Ne dispatch QUE sur match FRANC (mot de contrôle
  // reconnu, ou numéro en SELECT_PATIENT/AWAIT_ACTION) — les fenêtres overlap non
  // concluantes sont ignorées (pas de re-prompt sur bruit). Latch = un seul tir
  // par écoute armée (évite le double-commit sur fenêtres qui se chevauchent).
  const onCommand = useCallback((text: string) => {
    if (commandConsumedRef.current) return;
    const cur = stateRef.current.name;
    if (!COMMAND_LISTEN_STATES.has(cur)) return;
    const consume = (event: DialogueEvent) => {
      commandConsumedRef.current = true;
      setAwaitingCommand(false);
      disarmCommandRef.current();
      dispatchRef.current(event);
    };
    if (cur === 'AWAIT_ACTION') {
      // Hub (AC5) : `annule` universel → INTENT (mot-clé) → repli NUMÉRO.
      const ctrl = parseControl(text);
      if (ctrl.kind === 'cancel') { consume({ type: 'CONTROL', command: ctrl }); return; }
      const it = parseIntent(text);
      if (it.intent !== 'none') { consume({ type: 'INTENT', intent: it.intent, numero: it.numero }); return; }
      const numero = parseSpokenInt(text);
      if (numero !== null) consume({ type: 'SELECT', numero });
      return;
    }
    if (cur === 'SELECT_PATIENT') {
      // Sélection d'instance par NUMÉRO uniquement ; `annule` reste universel.
      const ctrl = parseControl(text);
      if (ctrl.kind === 'cancel') { consume({ type: 'CONTROL', command: ctrl }); return; }
      const numero = parseSpokenInt(text);
      if (numero !== null) consume({ type: 'SELECT', numero });
      return;
    }
    // CONFIRM_PATIENT (validation/annule) / AWAIT_COMMIT (validation/corrige/annule).
    const ctrl = parseControl(text);
    if (ctrl.kind !== 'none') consume({ type: 'CONTROL', command: ctrl });
  }, []);

  /** Entrée texte (US-VMPL-002) : même routage qu'une transcription vocale ; sert au pilotage SON de /vocal2 et de repli clavier. */
  const entendu = useCallback((texte: string) => {
    const t = texte.trim();
    if (!t) return;
    stopSpeakingRef.current();
    if (sessionRef.current) {
      sessionRef.current.destroy();
      sessionRef.current = null;
      setCapturing(false);
    }
    const name = stateRef.current.name;
    if (name === 'IDLE') routeCaptured(t, true);
    else if (name === 'CAPTURE') routeCaptured(t, false);
    else {
      commandConsumedRef.current = false;
      onCommand(t);
    }
  }, [routeCaptured, onCommand]);

  // « stop » = turn OFF (Option 1) capté par la boucle wake PENDANT que le système parle
  // ou en attente (wake actif seulement hors capture) → ferme le flux vocal
  // (stopFlow → ARMÉ). Pendant une capture active, c'est VoiceSessionManager qui
  // détecte « stop » (même stopFlow, DRY). Aucun 2e micro ouvert.
  const onStopWord = stopFlow;

  // Fix fondation (Bloc 1) : UN SEUL micro actif à la fois. L'ancien pause()
  // ne libérait pas le micro du wake → 2 getUserMedia simultanés sur le même
  // device → audio de capture dégradé (Vosk entendait du charabia). En coupant
  // le wake tant que `capturing` est vrai, la capture a le micro pour elle seule.
  // Pendant les états de contrôle, `capturing` reste false → le wake garde le micro
  // et écoute les commandes (mode commande Q-1, armé par maybeOpenResponseCapture).
  const { isListening, armCommand, disarmCommand } = useWakeWord({
    enabled: enabled && !capturing,
    onWakeWord,
    onStopWord,
    onCommand,
  });
  useEffect(() => { armCommandRef.current = armCommand; }, [armCommand]);
  useEffect(() => { disarmCommandRef.current = disarmCommand; }, [disarmCommand]);

  // Cleanup
  useEffect(() => () => { sessionRef.current?.destroy(); sessionRef.current = null; }, []);

  return {
    orbe: orbeStateFor(state.name),
    prompt: state.prompt,
    patientLabel: state.ctx.patient ? `${state.ctx.patient.prenom} ${state.ctx.patient.nom}` : null,
    capturing,
    awaitingCommand,
    busy,
    listening: isListening,
    error,
    phase: state.name,
    ctx: state.ctx,
    entendu,
  };
}
